import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import moment from "moment";
import DataTable from "components/dataTable/DataTable";
import Card from "components/card/Card";
import "./reservations.scss";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { GetAccommodationReservations } from "api/rpc/2024-04/facilityAdmin/accommodation/reservations";
import { AccommodationReservationStatusBadge } from "./EditReservation";
import { AccommodationReservationModal } from "components/accommodation/reservationModal/AccommodationReservationModal";
import { ConfirmReservationModal } from "components/accommodation/confirmReservationModal/ConfirmReservationModal";
import { useAccommodationReservation } from "hooks/accommodation/useAccommodationReservation";
import { IAccommodationReservation } from "./types";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";

export function Reservations() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [accommodationReservations, setAccommodationReservations] = useState(undefined);

  const {
    accommodationUnitTypes,
    mergedUnitTypes,
    selectedUnitTypes,
    accommodationCategories,
    customerPaymentMethods,
    currentStep,
    steps,
    rooms,
    reservationCompleteState,
    newReservationState,
    billingState,
    setNewReservationState,
    setBillingState,
    setRooms,
    setCurrentStep,
    handleContinue,
    handleBack,
    handleCheckboxChange,
    handleCustomerSelection,
    handleCardInputChange,
    handleBookReservation,
    handleOpenNewReservation,
    handleCloseConfirmation,
    handleCloseModal,
  } = useAccommodationReservation({
    onSuccess: () => void loadAccommodationReservations(),
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccommodationReservations(source.token);
    return () => source.cancel();
  }, []);

  async function loadAccommodationReservations(token?: CancelToken) {
    if (accommodationReservations !== undefined) {
      setAccommodationReservations(undefined);
    }
    const accommodationReservationsRes = await GetAccommodationReservations({ with_units: true }, false);

    if (token && token.reason) {
      return;
    }

    if (accommodationReservationsRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading reservations"));
      setAccommodationReservations([]);
      return;
    }

    setAccommodationReservations(accommodationReservationsRes.data);
  }

  const primaryAction = {
    content: t("Create Reservation"),
    action: handleOpenNewReservation,
  };

  return (
    <Page
      title={t("Reservations")}
      subtitle={t("Manage and keep track of your reservations in one place.")}
      primaryAction={primaryAction}
      // breadcrumbs={[
      //   { label: t("Accommodations"), url: "/admin/accommodation" },
      //   { label: t("Property Configuration"), url: "/admin/accommodation/configuration/locations" },
      //   { label: t("Reservations"), url: "/admin/accommodation/reservations" },
      // ]}
    >
      <hr />
      <Card title="Reservations">
        <div style={{ marginTop: "24px" }}>
          <DataTable
            noRoundedTop
            columns={[
              { label: t("Reservation ID") },
              { label: t("Name") },
              { label: t("Unit type") },
              { label: t("Arrival") },
              { label: t("Departure") },
              { label: t("Status") },
              { label: t("") },
            ]}
            loading={accommodationReservations === undefined}
          >
            {accommodationReservations?.map((reservation: IAccommodationReservation, index: number) => {
              const unitType = accommodationUnitTypes?.find(ut => ut.id === reservation?.unit_type_id);
              const customer = reservation.customer;

              return (
                <tr
                  key={index}
                  onClick={() => history.push("/admin/accommodation/reservations/" + String(reservation.id))}
                  className="clickable"
                >
                  <td>{reservation?.id}</td>
                  <td>{customer?.full_name}</td>

                  <td>{unitType?.title}</td>
                  <td>
                    {reservation?.arrival_date !== null
                      ? moment(reservation?.arrival_date, "YYYY-MM-DD").format("LL")
                      : ""}
                  </td>
                  <td>
                    {reservation?.departure_date !== null
                      ? moment(reservation?.departure_date, "YYYY-MM-DD").format("LL")
                      : ""}
                  </td>
                  <td>
                    <AccommodationReservationStatusBadge reservation={reservation} />
                  </td>
                  <td>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M1.14674 14.1201C1.18503 13.7755 1.20417 13.6032 1.2563 13.4422C1.30255 13.2993 1.3679 13.1633 1.45057 13.038C1.54375 12.8967 1.66633 12.7741 1.91148 12.5289L12.9167 1.5237C13.8372 0.603225 15.3296 0.603226 16.2501 1.5237C17.1705 2.44417 17.1705 3.93656 16.2501 4.85703L5.24481 15.8623C4.99966 16.1074 4.87709 16.23 4.73578 16.3232C4.61041 16.4058 4.47445 16.4712 4.33158 16.5174C4.17054 16.5696 3.99826 16.5887 3.65368 16.627L0.833374 16.9404L1.14674 14.1201Z"
                        stroke="#475467"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </div>
      </Card>

      <AccommodationReservationModal
        // States
        newReservationState={newReservationState}
        setNewReservationState={setNewReservationState}
        billingState={billingState}
        setBillingState={setBillingState}
        rooms={rooms}
        setRooms={setRooms}
        selectedUnitTypes={selectedUnitTypes}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        steps={steps}
        // Data Arrays
        accommodationCategories={accommodationCategories}
        mergedUnitTypes={mergedUnitTypes}
        customerPaymentMethods={customerPaymentMethods}
        // Handlers
        onClose={handleCloseModal}
        handleContinue={handleContinue}
        handleBack={handleBack}
        handleBookReservation={() => void handleBookReservation()}
        handleCustomerSelection={customer => handleCustomerSelection(customer)}
        handleCardInputChange={event =>
          handleCardInputChange(
            event as
              | StripeCardNumberElementChangeEvent
              | StripeCardExpiryElementChangeEvent
              | StripeCardCvcElementChangeEvent,
          )
        }
        handleCheckboxChange={unit => handleCheckboxChange(unit)}
      />

      <ConfirmReservationModal
        isOpen={reservationCompleteState}
        onClose={handleCloseConfirmation}
        newReservationState={newReservationState}
        accommodationUnitTypes={accommodationUnitTypes}
        selectedUnitTypes={selectedUnitTypes}
      />
    </Page>
  );
}
